/**
 * Operator registry for mesh and object operations.
 *
 * Modelled after Blender's operator system: each operator is a declarative
 * record with metadata (label, icon, category) and predicates that
 * determine when it appears and when it's enabled.  The registry is the
 * single source of truth consumed by the floating Operators panel, the
 * Operator Search palette (F3), and keyboard shortcut dispatch.
 *
 * Keyboard shortcuts are defined in keymap.ts and looked up by operator ID.
 * This file no longer carries shortcut strings — UI components call
 * getShortcutLabel(operator.id) from the keymap instead.
 *
 * Scope:
 * - Topology / modeling operators (extrude, inset, bridge, fill …)
 * - NOT base commands (delete, copy, paste, select-all, undo/redo)
 * - NOT scene-wide commands (add primitive, import, reset)
 *
 * Designed for both edit-mode and object-mode; object-mode operators can
 * be added here later with the same architecture.
 */

import type { LucideIcon } from "lucide-react";
import {
  ArrowDownToLine,
  Box,
  CloudFog,
  Cylinder,
  FlipHorizontal2,
  Layers,
  Lock,
  Pin,
  RefreshCw,
  Sparkles,
  Waves,
} from "lucide-react";
import {
  getSingleSelectionTarget,
  selectionTargetLeafIds,
  selectionToLeafObjectIds,
} from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import type { AppState } from "../app/storeTypes.ts";
import {
  BevelIcon,
  ConnectPathIcon,
  EdgeLoopIcon,
  EdgeSlideIcon,
  ExtrudeIcon,
  FillIcon,
  FlipNormalsIcon,
  GridFillIcon,
  HandleAlignedIcon,
  HandleAutoIcon,
  HandleFreeIcon,
  HandleVectorIcon,
  InsetIcon,
  MergeVerticesIcon,
  RecalcOutsideIcon,
  RipIcon,
  SeparateIcon,
  ShrinkFattenIcon,
  SkinRadiusIcon,
  SubdivideIcon,
  SurfacePolyIcon,
  ToggleCyclicIcon,
} from "../ui/icons3d.tsx";
import { EDGE_WIRE, isCurveObject, isLightObject } from "./types.ts";

// ---------------------------------------------------------------------------
// Context — derived from store state, passed to predicates
// ---------------------------------------------------------------------------

export interface OperatorContext {
  /** Current interaction mode bucket. */
  mode: "edit" | "object" | "pose";
  /** Edit-mode selection granularity (only meaningful when mode === "edit"). */
  granularity: "vertex" | "edge" | "face";
  /** Number of selected vertices (edit mode). */
  selectedVertexCount: number;
  /** Number of explicitly selected edges (edit mode). */
  selectedEdgeCount: number;
  /** Number of explicitly selected faces (edit mode). */
  selectedFaceCount: number;
  /** Whether the edited object is a curve. */
  isCurve: boolean;
  /** True when the edited or sole-selected object's topology contains at
   *  least one wire edge — i.e. it's a candidate for skin-modifier draping. */
  hasWireEdges: boolean;
  /** True when the edited or sole-selected object's topology contains at
   *  least one face — i.e. it's a candidate for cloth simulation. */
  hasFacedMesh: boolean;
  /** The active bone in pose mode (last clicked), or null. */
  activeBone: string | null;
  /** All selected bones in pose mode. */
  selectedBones: ReadonlySet<string>;
  /** Whether the active bone is locked. */
  boneIsLocked: boolean;
  /** Object ID of the mannequin being posed. */
  poseObjectId: string | null;
  /** Number of selected objects (object mode). */
  selectedObjectCount: number;
  /** True when at least one selected object (or the edited object) is a valid
   *  gravity simulation target: a curve with ≥ 2 points, a mesh containing
   *  wire edges, or a faced mesh (cloth). Used to gate Simulate Gravity. */
  hasGravityTarget: boolean;
}

type OperatorContextState = Pick<
  AppState,
  | "blendFiles"
  | "collections"
  | "editMode"
  | "lockedBones"
  | "mode"
  | "poseMode"
  | "sceneObjects"
  | "selectedObjectTargets"
>;

/**
 * Probe a SceneObject's topology for any wire edge. Used to determine
 * whether the Simulate Gravity operator should be available — wire edges
 * are the data backbone of skin-modifier rope chains.
 */
export function objectHasWireEdges(
  objectId: string | null | undefined,
  sceneObjects: ReadonlyMap<string, { topology?: { edgeFlags: Uint8Array } }>,
): boolean {
  if (!objectId) return false;
  const flags = sceneObjects.get(objectId)?.topology?.edgeFlags;
  if (!flags) return false;
  for (let i = 0; i < flags.length; i++) {
    if (flags[i] & EDGE_WIRE) return true;
  }
  return false;
}

/**
 * Probe a SceneObject's topology for at least one face. Used to determine
 * whether Simulate Gravity's cloth path is applicable. `offsets.length > 1`
 * means there's at least one face (offsets is faceCount + 1 long).
 */
export function objectHasFaces(
  objectId: string | null | undefined,
  sceneObjects: ReadonlyMap<string, { topology?: { offsets: Uint32Array } }>,
): boolean {
  if (!objectId) return false;
  const offsets = sceneObjects.get(objectId)?.topology?.offsets;
  return offsets ? offsets.length > 1 : false;
}

export function buildOperatorContextFromState(
  state: OperatorContextState,
  selectedLeafObjectIds?: Iterable<string>,
): OperatorContext {
  const inEdit = state.mode === "edit" && state.editMode != null;
  const inPose = state.poseMode != null;
  const activeBone = state.poseMode?.activeBone ?? null;
  const selectedBones = state.poseMode?.selectedBones ?? new Set<string>();
  const poseObjectId = state.poseMode?.objectId ?? null;
  const boneIsLocked =
    poseObjectId && activeBone
      ? (state.lockedBones.get(poseObjectId)?.has(activeBone) ?? false)
      : false;

  // Pick the object whose wire-edge state matters: edit-mode target first,
  // otherwise the single-selected object in object mode.
  let probeId: string | null = null;
  if (state.editMode && !state.editMode.isCurve)
    probeId = state.editMode.objectId;
  else if (!inEdit && !inPose) {
    const target = getSingleSelectionTarget(state.selectedObjectTargets);
    if (target?.kind === "sceneObject") {
      probeId = target.id;
    }
  }
  const hasWireEdges = objectHasWireEdges(probeId, state.sceneObjects);
  const hasFacedMesh = objectHasFaces(probeId, state.sceneObjects);

  // Scan all selected objects to determine whether any is a valid gravity
  // target. Edit mode uses the already-computed isCurve / hasWireEdges /
  // hasFacedMesh. Object mode must check every member of the multi-selection.
  let hasGravityTarget = false;
  if (inEdit) {
    hasGravityTarget =
      (state.editMode?.isCurve ?? false) || hasWireEdges || hasFacedMesh;
  } else if (!inPose) {
    const leafIds = selectedLeafObjectIds
      ? Array.from(selectedLeafObjectIds)
      : selectionToLeafObjectIds(
          state.blendFiles,
          state.collections,
          state.selectedObjectTargets,
        );
    for (const id of leafIds) {
      const obj = state.sceneObjects.get(id);
      if (!obj || isLightObject(obj) || obj.objectKind === "mannequin")
        continue;
      if (
        isCurveObject(obj) &&
        obj.curveData &&
        obj.curveData.points.length >= 2
      ) {
        hasGravityTarget = true;
        break;
      }
      if (objectHasWireEdges(id, state.sceneObjects)) {
        hasGravityTarget = true;
        break;
      }
      if (objectHasFaces(id, state.sceneObjects)) {
        hasGravityTarget = true;
        break;
      }
    }
  }

  return {
    mode: inPose ? "pose" : inEdit ? "edit" : "object",
    granularity: state.editMode?.granularity ?? "vertex",
    selectedVertexCount: state.editMode?.selectedVertices.size ?? 0,
    selectedEdgeCount: state.editMode?.selectedEdges.size ?? 0,
    selectedFaceCount: state.editMode?.selectedFaces.size ?? 0,
    isCurve: state.editMode?.isCurve ?? false,
    hasWireEdges,
    hasFacedMesh,
    activeBone,
    selectedBones,
    boneIsLocked,
    poseObjectId,
    selectedObjectCount: state.selectedObjectTargets.length,
    hasGravityTarget,
  };
}

/** Build an OperatorContext from the current store snapshot. */
export function buildOperatorContext(): OperatorContext {
  return buildOperatorContextFromState(useStore.getState());
}

export function activeEditSelectionCount(ctx: OperatorContext): number {
  if (ctx.granularity === "face") return ctx.selectedFaceCount;
  if (ctx.granularity === "edge") return ctx.selectedEdgeCount;
  return ctx.selectedVertexCount;
}

export function canExtrudeSelection(ctx: OperatorContext): boolean {
  return activeEditSelectionCount(ctx) >= 1;
}

export function canInsetSelection(ctx: OperatorContext): boolean {
  if (ctx.granularity === "face") return ctx.selectedFaceCount >= 1;
  if (ctx.granularity === "edge") return ctx.selectedEdgeCount >= 3;
  return ctx.selectedVertexCount >= 3;
}

export function canBevelSelection(ctx: OperatorContext): boolean {
  if (ctx.granularity === "face") return ctx.selectedFaceCount >= 1;
  if (ctx.granularity === "edge") return ctx.selectedEdgeCount >= 1;
  return ctx.selectedVertexCount >= 2;
}

export function canFillSelection(ctx: OperatorContext): boolean {
  if (ctx.granularity === "face") return false;
  if (ctx.granularity === "edge") return ctx.selectedEdgeCount >= 3;
  return ctx.selectedVertexCount >= 2;
}

export function canBridgeEdgeLoopsSelection(ctx: OperatorContext): boolean {
  return ctx.granularity === "edge" && ctx.selectedEdgeCount >= 6;
}

export function canGridFillSelection(ctx: OperatorContext): boolean {
  if (ctx.granularity === "face") return false;
  if (ctx.granularity === "edge") return ctx.selectedEdgeCount >= 4;
  return ctx.selectedVertexCount >= 4;
}

export function canConnectVertexPathSelection(ctx: OperatorContext): boolean {
  return ctx.granularity === "vertex" && ctx.selectedVertexCount >= 2;
}

export function canSeparateSelection(ctx: OperatorContext): boolean {
  if (ctx.granularity === "face") return ctx.selectedFaceCount >= 1;
  if (ctx.granularity === "edge") return ctx.selectedEdgeCount >= 1;
  return ctx.selectedVertexCount >= 3;
}

// ---------------------------------------------------------------------------
// Operator definition
// ---------------------------------------------------------------------------

export type OperatorCategory = "topology" | "pose" | "object";

export interface Operator {
  /** Unique, stable identifier (used as localStorage key for favorites and as keymap action ID). */
  id: string;
  /** Human-readable label shown in the panel. */
  label: string;
  /** Icon component rendered beside the label (Lucide or custom SVG). */
  icon: LucideIcon | React.ComponentType<{ size?: number | string }>;
  /** Grouping category for the search panel. */
  category: OperatorCategory;
  /** When true, clicking toggles a persistent active state instead of executing immediately. */
  toggle?: boolean;
  /** Should this operator appear at all given the current context? */
  isRelevant: (ctx: OperatorContext) => boolean;
  /** Is the operator invokable right now? (greyed out when false). */
  isEnabled: (ctx: OperatorContext) => boolean;
  /** Execute the operator. Accesses store directly via useStore.getState(). */
  execute: () => void;
}

// ---------------------------------------------------------------------------
// Category metadata
// ---------------------------------------------------------------------------

export const OPERATOR_CATEGORY_LABELS: Record<OperatorCategory, string> = {
  topology: "Topology",
  pose: "Pose",
  object: "Object",
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const OPERATORS: readonly Operator[] = [
  {
    id: "extrude",
    label: "Extrude Region",
    icon: ExtrudeIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit",
    isEnabled: canExtrudeSelection,
    execute: () => {
      const s = useStore.getState();
      const em = s.editMode;
      if (!em) return;
      if (em.isCurve) {
        // Curve: arm so the next tap extends the curve
        if (s.activeOperator === "extrude") s.setActiveOperator(null);
        else s.setActiveOperator("extrude");
      } else {
        s.extrudeSelection();
      }
    },
  },
  {
    id: "extrude_along_normals",
    label: "Extrude Along Normals",
    icon: ShrinkFattenIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: canExtrudeSelection,
    execute: () => {
      useStore.getState().extrudeAlongNormalsSelection();
    },
  },
  {
    id: "extrude_individual",
    label: "Extrude Individual Faces",
    icon: Layers,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) =>
      ctx.granularity === "face" && ctx.selectedFaceCount >= 1,
    execute: () => {
      useStore.getState().extrudeIndividualFacesSelection();
    },
  },
  {
    id: "extrude_manifold",
    label: "Extrude Manifold",
    icon: ExtrudeIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) =>
      ctx.granularity === "face" && ctx.selectedFaceCount >= 1,
    execute: () => {
      useStore.getState().extrudeManifoldSelection();
    },
  },
  {
    id: "inset_faces",
    label: "Inset Faces",
    icon: InsetIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: canInsetSelection,
    execute: () => {
      const s = useStore.getState();
      if (s.activeOperator === "inset_faces") {
        s.setActiveOperator(null);
      } else {
        s.setActiveOperator("inset_faces");
      }
    },
  },
  {
    id: "bevel",
    label: "Bevel",
    icon: BevelIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: canBevelSelection,
    execute: () => {
      const s = useStore.getState();
      if (s.activeOperator === "bevel") {
        s.setActiveOperator(null);
      } else {
        s.setActiveOperator("bevel");
      }
    },
  },
  {
    id: "smooth",
    label: "Smooth Vertices",
    icon: Waves,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) => activeEditSelectionCount(ctx) >= 1,
    execute: () => {
      useStore.getState().smoothSelection();
    },
  },
  {
    id: "fill",
    label: "Fill",
    icon: FillIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: canFillSelection,
    execute: () => {
      useStore.getState().fillSelection();
    },
  },
  {
    id: "bridge_edge_loops",
    label: "Bridge Edge Loops",
    icon: FillIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: canBridgeEdgeLoopsSelection,
    execute: () => {
      useStore.getState().bridgeEdgeLoops();
    },
  },
  {
    id: "grid_fill",
    label: "Grid Fill",
    icon: GridFillIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: canGridFillSelection,
    execute: () => {
      useStore.getState().gridFillSelection();
    },
  },
  {
    id: "edge_slide",
    label: "Edge Slide",
    icon: EdgeSlideIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) =>
      ctx.granularity === "edge" && ctx.selectedEdgeCount >= 1,
    execute: () => {
      const s = useStore.getState();
      if (s.commandBuffer?.type === "edgeSlide") {
        s.cancelCommand();
      } else {
        s.enterCommand("edgeSlide");
      }
    },
  },
  {
    id: "subdivide",
    label: "Subdivide",
    icon: SubdivideIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit",
    isEnabled: (ctx) => activeEditSelectionCount(ctx) >= 1,
    execute: () => {
      useStore.getState().subdivideSelection();
    },
  },
  {
    id: "merge_vertices",
    label: "Merge…",
    icon: MergeVerticesIcon,
    category: "topology",
    isRelevant: (ctx) =>
      ctx.mode === "edit" && !ctx.isCurve && ctx.granularity === "vertex",
    isEnabled: (ctx) => ctx.selectedVertexCount >= 2,
    execute: () => {
      useStore.getState().setMergeMenuOpen(true);
    },
  },
  {
    id: "insert_edge_loop",
    label: "Insert Edge Loop",
    icon: EdgeLoopIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) => ctx.mode === "edit",
    execute: () => {
      const s = useStore.getState();
      if (s.commandBuffer?.type === "edgeLoopInsert") {
        s.cancelCommand();
      } else {
        s.enterEdgeLoopInsert();
      }
    },
  },
  {
    id: "flip_normals",
    label: "Flip Normals",
    icon: FlipNormalsIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) => activeEditSelectionCount(ctx) >= 1,
    execute: () => {
      const s = useStore.getState();
      s.pushUndo();
      s.flipNormals();
    },
  },
  {
    id: "recalculate_outside",
    label: "Recalculate Outside",
    icon: RecalcOutsideIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) => activeEditSelectionCount(ctx) >= 1,
    execute: () => {
      const s = useStore.getState();
      s.pushUndo();
      s.recalculateOutside();
    },
  },
  {
    id: "connect_vertex_path",
    label: "Connect Vertex Path",
    icon: ConnectPathIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: canConnectVertexPathSelection,
    execute: () => {
      useStore.getState().connectVertexPath();
    },
  },
  {
    id: "rip",
    label: "Rip",
    icon: RipIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) => activeEditSelectionCount(ctx) >= 1,
    execute: () => {
      useStore.getState().ripSelection(0, 0);
    },
  },
  {
    id: "separate",
    label: "Separate",
    icon: SeparateIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: canSeparateSelection,
    execute: () => {
      useStore.getState().separateSelection();
    },
  },
  {
    id: "clean_up",
    label: "Clean Up",
    icon: Sparkles,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: () => true,
    execute: () => {
      useStore.getState().cleanUpMesh();
    },
  },
  {
    id: "shrink_fatten",
    label: "Shrink/Fatten",
    icon: ShrinkFattenIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) => activeEditSelectionCount(ctx) >= 1,
    execute: () => {
      const s = useStore.getState();
      if (s.activeOperator === "shrink_fatten") {
        s.setActiveOperator(null);
      } else {
        s.setActiveOperator("shrink_fatten");
      }
    },
  },
  {
    id: "relax_to_surface",
    label: "Relax to Surface",
    icon: Waves,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) => activeEditSelectionCount(ctx) >= 1,
    execute: () => {
      useStore.getState().relaxSelectionToSurface();
    },
  },
  {
    id: "skin_radius",
    label: "Skin Radius",
    icon: SkinRadiusIcon,
    category: "topology",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "edit" && !ctx.isCurve,
    isEnabled: (ctx) => activeEditSelectionCount(ctx) >= 1,
    execute: () => {
      const s = useStore.getState();
      if (s.activeOperator === "skin_radius") {
        s.setActiveOperator(null);
      } else {
        s.setActiveOperator("skin_radius");
      }
    },
  },
  // --- Curve edit operators ---
  {
    id: "set_handle_auto",
    label: "Handle: Auto",
    icon: HandleAutoIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && ctx.isCurve,
    isEnabled: (ctx) => ctx.selectedVertexCount >= 1,
    execute: () => {
      const s = useStore.getState();
      s.pushUndo();
      s.setCurveHandleType("auto");
    },
  },
  {
    id: "set_handle_vector",
    label: "Handle: Vector",
    icon: HandleVectorIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && ctx.isCurve,
    isEnabled: (ctx) => ctx.selectedVertexCount >= 1,
    execute: () => {
      const s = useStore.getState();
      s.pushUndo();
      s.setCurveHandleType("vector");
    },
  },
  {
    id: "set_handle_aligned",
    label: "Handle: Aligned",
    icon: HandleAlignedIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && ctx.isCurve,
    isEnabled: (ctx) => ctx.selectedVertexCount >= 1,
    execute: () => {
      const s = useStore.getState();
      s.pushUndo();
      s.setCurveHandleType("aligned");
    },
  },
  {
    id: "set_handle_free",
    label: "Handle: Free",
    icon: HandleFreeIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && ctx.isCurve,
    isEnabled: (ctx) => ctx.selectedVertexCount >= 1,
    execute: () => {
      const s = useStore.getState();
      s.pushUndo();
      s.setCurveHandleType("free");
    },
  },
  {
    id: "toggle_cyclic",
    label: "Toggle Cyclic",
    icon: ToggleCyclicIcon,
    category: "topology",
    isRelevant: (ctx) => ctx.mode === "edit" && ctx.isCurve,
    isEnabled: (ctx) => ctx.mode === "edit" && ctx.isCurve,
    execute: () => {
      const s = useStore.getState();
      s.pushUndo();
      s.toggleCurveCyclic();
    },
  },
  // --- Pose mode operators ---
  {
    id: "lock_joint",
    label: "Toggle Joint Lock",
    icon: Lock,
    category: "pose",
    isRelevant: (ctx) => ctx.mode === "pose",
    isEnabled: (ctx) => ctx.selectedBones.size > 0,
    execute: () => {
      const s = useStore.getState();
      const pm = s.poseMode;
      if (pm?.objectId) {
        s.pushUndo();
        for (const boneName of pm.selectedBones) {
          s.toggleBoneLock(pm.objectId, boneName);
        }
      }
    },
  },
  {
    id: "pin_joint",
    label: "Toggle Joint Pin",
    icon: Pin,
    category: "pose",
    isRelevant: (ctx) => ctx.mode === "pose",
    isEnabled: (ctx) => ctx.selectedBones.size > 0,
    execute: () => {
      const s = useStore.getState();
      const pm = s.poseMode;
      if (pm?.objectId) {
        s.pushUndo();
        for (const boneName of pm.selectedBones) {
          s.toggleBonePin(pm.objectId, boneName);
        }
      }
    },
  },
  {
    id: "mirror_pose",
    label: "Mirror Pose",
    icon: FlipHorizontal2,
    category: "pose",
    isRelevant: (ctx) => ctx.mode === "pose",
    isEnabled: (ctx) => {
      for (const bone of ctx.selectedBones) {
        if (bone.includes("L") || bone.includes("R")) return true;
      }
      return false;
    },
    execute: () => {
      const s = useStore.getState();
      const pm = s.poseMode;
      if (pm?.objectId) {
        for (const boneName of pm.selectedBones) {
          if (boneName.includes("L") || boneName.includes("R")) {
            s.mirrorBonePose(pm.objectId, boneName);
          }
        }
      }
    },
  },
  {
    id: "drop",
    label: "Drop",
    icon: ArrowDownToLine,
    category: "object",
    isRelevant: (ctx) => ctx.mode === "pose" || ctx.mode === "object",
    isEnabled: (ctx) =>
      ctx.mode === "pose"
        ? ctx.selectedBones.size > 0
        : ctx.selectedObjectCount > 0,
    execute: () => {
      const s = useStore.getState();
      const pm = s.poseMode;
      if (pm?.objectId) {
        s.drop(pm.objectId);
        return;
      }
      // Dispatch by target kind so collection (group generator) targets
      // drop as a rigid unit via the group transform — iterating leaf ids
      // would land each child independently and destroy their relative
      // offsets within the group.
      const seen = new Set<string>();
      for (const target of s.selectedObjectTargets) {
        if (target.kind === "collection") {
          s.dropCollection(target.id);
          continue;
        }
        for (const id of selectionTargetLeafIds(
          s.blendFiles,
          s.collections,
          target,
        )) {
          if (seen.has(id)) continue;
          seen.add(id);
          s.drop(id);
        }
      }
    },
  },
  {
    id: "simulate_gravity",
    label: "Simulate Gravity",
    icon: CloudFog,
    category: "object",
    isRelevant: (ctx) =>
      ctx.mode === "object" ||
      (ctx.mode === "edit" &&
        (ctx.isCurve || ctx.hasWireEdges || ctx.hasFacedMesh)),
    isEnabled: (ctx) => {
      if (ctx.mode === "edit")
        return ctx.isCurve || ctx.hasWireEdges || ctx.hasFacedMesh;
      if (ctx.mode === "object") return ctx.hasGravityTarget;
      return false;
    },
    // Opening the menu runs an immediate rigid (compliance = 0) simulation
    // so the user sees a rope on first click, then scrubs the Elasticity
    // slider to soften it. Baselines captured on open.
    execute: () => {
      useStore.getState().setGravityMenuOpen(true);
    },
  },
  {
    id: "relax_bones",
    label: "Relax Bones",
    icon: Waves,
    category: "pose",
    isRelevant: (ctx) => ctx.mode === "pose",
    isEnabled: (ctx) => ctx.selectedBones.size > 0,
    execute: () => {
      useStore.getState().setRelaxMenuOpen(true);
    },
  },
  {
    id: "reset_pose",
    label: "Reset All Joints",
    icon: RefreshCw,
    category: "pose",
    isRelevant: (ctx) => ctx.mode === "pose",
    isEnabled: (ctx) => ctx.poseObjectId != null,
    execute: () => {
      const s = useStore.getState();
      const pm = s.poseMode;
      if (pm?.objectId) s.resetPose(pm.objectId);
    },
  },
  {
    id: "surface_box",
    label: "Surface Box",
    icon: Box,
    category: "object",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "object",
    isEnabled: () => true,
    // Toggling: arms the modal handler (the click-drag interaction lives in
    // viewport/SurfaceBoxHandler). Keyboard / F3 invocation routes through
    // the same arming path so the behaviour is identical from any entry.
    execute: () => {
      const s = useStore.getState();
      s.armToolOperator(
        s.activeOperator === "surface_box" ? null : "surface_box",
      );
    },
  },
  {
    id: "surface_cylinder",
    label: "Surface Cylinder",
    icon: Cylinder,
    category: "object",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "object",
    isEnabled: () => true,
    execute: () => {
      const s = useStore.getState();
      s.armToolOperator(
        s.activeOperator === "surface_cylinder" ? null : "surface_cylinder",
      );
    },
  },
  {
    id: "surface_poly",
    label: "Surface Poly",
    icon: SurfacePolyIcon,
    category: "object",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "object",
    isEnabled: () => true,
    execute: () => {
      const s = useStore.getState();
      s.armToolOperator(
        s.activeOperator === "surface_poly" ? null : "surface_poly",
      );
    },
  },
  {
    id: "surface_inset",
    label: "Surface Inset",
    icon: InsetIcon,
    category: "object",
    toggle: true,
    isRelevant: (ctx) => ctx.mode === "object",
    isEnabled: () => true,
    execute: () => {
      const s = useStore.getState();
      s.armToolOperator(
        s.activeOperator === "surface_inset" ? null : "surface_inset",
      );
    },
  },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Look an operator up by its stable registry id. Returns `null` when the
 *  id is unknown — callers (e.g. the generator panel's operator shortcuts)
 *  should treat missing ids as a silent no-op rather than a throw, so a
 *  stale reference in a generator definition can't break the sidebar. */
export function getOperator(id: string): Operator | null {
  for (const op of OPERATORS) if (op.id === id) return op;
  return null;
}

/** All operators relevant to the given context. */
export function getRelevantOperators(ctx: OperatorContext): Operator[] {
  return OPERATORS.filter((a) => a.isRelevant(ctx));
}

/** Partition relevant operators into [favorites, others]. */
export function partitionOperators(
  ctx: OperatorContext,
  favoriteIds: ReadonlySet<string>,
): { favorites: Operator[]; others: Operator[] } {
  const relevant = getRelevantOperators(ctx);
  const favorites: Operator[] = [];
  const others: Operator[] = [];
  for (const a of relevant) {
    if (favoriteIds.has(a.id)) favorites.push(a);
    else others.push(a);
  }
  return { favorites, others };
}
